import React, { useState, useEffect } from 'react';
import { Form, Button, Select, Table, Popconfirm, message, Spin, Modal, Divider } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  fetchAssignments,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  fetchEligibleTranslators,
  fetchProjectById, // Thunk to fetch a project by ID
} from '../../features/Projects/projectSlice';

const { Option } = Select;

const AssignmentManagement = () => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const [bulkForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editForm] = Form.useForm();

  const { projects, assignments, assignmentsLoading, eligibleTranslators } = useSelector(
    (state) => state.projects
  );

  // Try to find the project in Redux.
  let project = projects.find((p) => p.id === parseInt(projectId));

  // If project is not found, fetch it.
  useEffect(() => {
    if (!project && projectId) {
      dispatch(fetchProjectById(projectId));
    }
  }, [project, projectId, dispatch]);

  // Get eligible data from Redux state using project id.
  const eligibleData = project && eligibleTranslators[project.id] ? eligibleTranslators[project.id] : {};
  const translators = eligibleData.eligible_translators || [];
  const services = eligibleData.project_services || [];
  const targetLanguages = eligibleData.project_target_languages || [];

  // Once the project is available, fetch eligible translators and assignments.
  useEffect(() => {
    if (project) {
      dispatch(fetchEligibleTranslators(project.id));
      dispatch(fetchAssignments(project.id));
    }
  }, [project, dispatch]); // Remove eligibleTranslators from dependency array

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      await dispatch(createAssignment({ projectId: project.id, data: values })).unwrap();
      message.success('Assignments created successfully');
      bulkForm.resetFields();
      dispatch(fetchAssignments(project.id));
    } catch (error) {
      message.error('Failed to create assignments');
    }
    setSubmitting(false);
  };

  const handleUpdate = async (values) => {
    setSubmitting(true);
    try {
      await dispatch(
        updateAssignment({
          projectId: project.id,
          assignmentId: editingAssignment.id,
          data: values,
        })
      ).unwrap();
      message.success('Assignment updated successfully');
      setEditingAssignment(null);
      dispatch(fetchAssignments(project.id));
    } catch (error) {
      message.error('Failed to update assignment');
    }
    setSubmitting(false);
  };

  const handleDelete = async (assignmentId) => {
    try {
      await dispatch(deleteAssignment({ projectId: project.id, assignmentId })).unwrap();
      message.success('Assignment deleted successfully');
      dispatch(fetchAssignments(project.id));
    } catch (error) {
      message.error('Failed to delete assignment');
    }
  };

  if (!project) {
    return <Spin />;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>Manage Assignments for {project.name}</h2>

      <h3>Existing Assignments</h3>
      <Table
        dataSource={assignments[project.id] || []}
        columns={[
          {
            title: 'Translator',
            dataIndex: ['translator', 'name'],
            key: 'translator',
          },
          {
            title: 'Service',
            dataIndex: ['service', 'name'],
            key: 'service',
          },
          {
            title: 'Target Languages',
            key: 'target_languages',
            render: (_, record) =>
              record.target_languages.map((lang) => lang.name).join(', '),
          },
          {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
              <>
                <Button type="link" onClick={() => {
                  setEditingAssignment(record);
                  editForm.setFieldsValue({
                    translator_id: record.translator.id,
                    service_id: record.service.id,
                    target_language_ids: record.target_languages.map((lang) => lang.id),
                  });
                }}>
                  Edit
                </Button>
                <Popconfirm
                  title="Are you sure you want to delete this assignment?"
                  onConfirm={() => handleDelete(record.id)}
                >
                  <Button type="link" danger>
                    Delete
                  </Button>
                </Popconfirm>
              </>
            ),
          },
        ]}
        rowKey="id"
        loading={assignmentsLoading}
      />

      <h3 style={{ marginTop: '40px' }}>Bulk Create Assignments</h3>
      <Form
        form={bulkForm}
        name="bulk_assignments"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.List name="assignments">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <div
                  key={field.key}
                  style={{
                    display: 'flex',
                    marginBottom: 8,
                    alignItems: 'center',
                  }}
                >
                  <Form.Item
                    {...field}
                    name={[field.name, 'translator_id']}
                    fieldKey={[field.fieldKey, 'translator_id']}
                    rules={[{ required: true, message: 'Select translator' }]}
                    style={{ flex: 1, marginRight: 8 }}
                  >
                    <Select placeholder="Select translator">
                      {translators.map((t) => (
                        <Option key={t.id} value={t.id}>
                          {t.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'service_id']}
                    fieldKey={[field.fieldKey, 'service_id']}
                    rules={[{ required: true, message: 'Select service' }]}
                    style={{ flex: 1, marginRight: 8 }}
                  >
                    <Select placeholder="Select service">
                      {services.map((s) => (
                        <Option key={s.id} value={s.id}>
                          {s.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'target_language_ids']}
                    fieldKey={[field.fieldKey, 'target_language_ids']}
                    rules={[{ required: true, message: 'Select target languages' }]}
                    style={{ flex: 2, marginRight: 8 }}
                  >
                    <Select mode="multiple" placeholder="Select target languages">
                      {targetLanguages.map((lang) => (
                        <Option key={lang.id} value={lang.id}>
                          {lang.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Button onClick={() => remove(field.name)} type="link" danger>
                    Remove
                  </Button>
                </div>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block>
                  Add Assignment
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Submit Assignments
          </Button>
        </Form.Item>
      </Form>

      <Modal
        title="Edit Assignment"
        visible={!!editingAssignment}
        onCancel={() => setEditingAssignment(null)}
        footer={null}
      >
        <Form
          form={editForm}
          onFinish={handleUpdate}
          layout="vertical"
          initialValues={{
            translator_id: editingAssignment?.translator?.id,
            service_id: editingAssignment?.service?.id,
            target_language_ids: editingAssignment?.target_languages?.map((lang) => lang.id),
          }}
        >
          <Form.Item
            name="translator_id"
            label="Translator"
            rules={[{ required: true, message: 'Select translator' }]}
          >
            <Select placeholder="Select translator">
              {translators.map((t) => (
                <Option key={t.id} value={t.id}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="service_id"
            label="Service"
            rules={[{ required: true, message: 'Select service' }]}
          >
            <Select placeholder="Select service">
              {services.map((s) => (
                <Option key={s.id} value={s.id}>
                  {s.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="target_language_ids"
            label="Target Languages"
            rules={[{ required: true, message: 'Select target languages' }]}
          >
            <Select mode="multiple" placeholder="Select target languages">
              {targetLanguages.map((lang) => (
                <Option key={lang.id} value={lang.id}>
                  {lang.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Update Assignment
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssignmentManagement;
